import { FC, MouseEvent, useMemo } from 'react';

import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';

import { JSONHelpers } from 'helpers/JSONHelpers';

import { Avatar } from 'components/avatar/Avatar';
import { TipTapViewer } from 'components/tiptap/TipTapViewer';
import { DateService } from 'services/DateService';
import { RouterService } from 'services/RouterService';

import { ProjectTeaserLatestMessage as LatestMessage } from '@/collections/projects/endpoints/teaser';

import cx from './ProjectTeaserLatestMessage.module.scss';

export const ProjectTeaserLatestMessage: FC<{ message: LatestMessage; projectUrl: string }> = ({
    message,
    projectUrl,
}) => {
    const author = message.createdByCompany?.name || message.createdBy?.name || message.createdBy?.email || 'Unknown';

    const content = useMemo(() => JSONHelpers.parse(message.content), [message.content]);

    const openChannel = async (event: MouseEvent) => {
        if (!message.channel) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        await RouterService.push(`${projectUrl}?channel=${message.channel.id}`);
    };

    const body = (
        <Stack gap={4} justify="center" onClick={openChannel} className={message.channel ? cx.clickable : undefined}>
            <Box className={cx.body} data-unread={!message.read || undefined}>
                {content ? <TipTapViewer content={content} files={[]} /> : message.label}
            </Box>

            <Group gap={6} wrap="nowrap" mt="xs">
                <Avatar
                    company={message.createdByCompany}
                    user={message.createdByCompany ? undefined : message.createdBy}
                    size={16}
                />

                <Text fz="sm" c="gray.6" truncate>
                    {author}
                </Text>

                <Text fz="sm" c="gray.6" style={{ flexShrink: 0 }}>
                    {DateService.formatDistanceToNow(message.createdAt)}
                </Text>

                {message.channel && (
                    <Text fz="sm" c="gray.6">
                        in #{message.channel.name}
                    </Text>
                )}
            </Group>
        </Stack>
    );

    if (!message.channel) {
        return body;
    }

    return <Tooltip.Floating label={`Open #${message.channel.name} ->`}>{body}</Tooltip.Floating>;
};
